import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, Users, ExternalLink } from "lucide-react";

const PROJECTS = [
  {
    name: "Sara Palmer DDS",
    industry: "Dental / Healthcare",
    services: "Custom Website + Local SEO",
    traffic: "+248%",
    leads: "+312%",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2026/03/desktop-4.png",
    href: "https://www.sarahpalmerdds.com/",
    caseHref: "https://portfolio.echowingsmedia.com/portfolio/sarapalmerdds/",
  },
  {
    name: "Lakeshore Express",
    industry: "Logistics / Transport",
    services: "Website + Brand System",
    traffic: "+196%",
    leads: "+184%",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2026/03/desktop-2.png",
    href: "https://lakeshore.express/",
    caseHref: "https://portfolio.echowingsmedia.com/portfolio/lakeshore-express/",
  },
  {
    name: "Jason Paige",
    industry: "Music / Personal Brand",
    services: "Premium Portfolio + GEO",
    traffic: "+285%",
    leads: "+220%",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2026/03/desktop-1.png",
    href: "https://jasonpaige.com/",
    caseHref: "https://portfolio.echowingsmedia.com/portfolio/jasonpaige/",
  },
  {
    name: "MyRoofNexus",
    industry: "Roofing",
    services: "Website + Local SEO + GEO",
    traffic: "+340%",
    leads: "+412%",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2025/09/desktop-1-1.png",
    href: "https://myroofnexus.com/",
    caseHref: "https://portfolio.echowingsmedia.com/portfolio/myroofnexus/",
  },
  {
    name: "Build With Koda",
    industry: "Construction / Home Building",
    services: "Custom Build + Conversion",
    traffic: "+218%",
    leads: "+267%",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2025/09/desktop-1.png",
    href: "https://buildwithkoda.com/",
    caseHref: "https://portfolio.echowingsmedia.com/portfolio/buildwithkoda/",
  },
];

export default function FeaturedProjects() {
  return (
    <section
      id="projects"
      data-testid="featured-projects"
      className="section relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Featured Work
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Outcomes, not just websites.
            </h2>
          </div>
          <p className="max-w-md text-white/55">
            Real businesses we&apos;ve partnered with — every project engineered to
            grow rankings, leads and revenue, measured in real numbers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map((p, i) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`project-card-${i}`}
              className="group rounded-3xl border border-white/10 bg-white/[0.025] hover:bg-white/[0.04] hover:border-cyan-500/30 transition-all duration-500 flex flex-col overflow-hidden"
            >
              {/* Image — framed, not background */}
              <a
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-image-link-${i}`}
                className="relative block m-3 mb-0 rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-white/85 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ExternalLink className="w-3 h-3" /> Visit Live
                </span>
              </a>

              {/* Content section */}
              <div className="flex flex-col flex-1 p-6">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold">
                  <span className="text-cyan-400">{p.industry}</span>
                </div>

                <h3 className="mt-2 font-display text-2xl font-semibold text-white">
                  {p.name}
                </h3>
                <p className="mt-1.5 text-sm text-white/55">{p.services}</p>

                {/* Result stats — always visible */}
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                      <TrendingUp className="w-3 h-3 text-cyan-400" />
                      Traffic
                    </div>
                    <div className="mt-0.5 font-display text-lg font-bold text-gradient-brand">
                      {p.traffic}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                      <Users className="w-3 h-3 text-cyan-400" />
                      Leads
                    </div>
                    <div className="mt-0.5 font-display text-lg font-bold text-gradient-brand">
                      {p.leads}
                    </div>
                  </div>
                </div>

                <a
                  href={p.caseHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`project-cta-${i}`}
                  className="mt-6 inline-flex items-center justify-between gap-2 text-sm font-semibold text-white hover:text-cyan-400 transition-colors group/cta border-t border-white/5 pt-4"
                >
                  <span>View Case Study</span>
                  <span className="inline-flex w-8 h-8 rounded-full bg-white/5 border border-white/10 items-center justify-center group-hover/cta:bg-gradient-to-r group-hover/cta:from-blue-600 group-hover/cta:to-cyan-400 group-hover/cta:border-transparent transition-all">
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover/cta:rotate-0" />
                  </span>
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
